import { atom } from 'jotai';
import { Area } from '@norlys-a-s/norlys-play-kmm';
import { CmdError, BaseError } from './contexts/error-context/error-context';
import { KMMError } from '@norlys-a-s/norlys-play-kmm';
import { IconTypes } from '@smart-tv/hooks/use-toast/use-toast';
import { NpvrNotificationStateInfo } from '@smart-tv/typings/npvr';

export enum FocusKeyPrevSource {
  TvGuideAsset = 'tv-guide-asset',
  ContentSlider = 'ContentSlider'
}

type FocusKeyPrev = {
  focusKeyPrev: string | null;
  isLiveTV: boolean;
  source?: FocusKeyPrevSource | null;
};
export const focusKeyPrevAtom = atom<FocusKeyPrev | null>(null);

export const isHeroRenderedAtom = atom<boolean | undefined>(undefined);

export const pageAreaAtom = atom<Area | undefined>(undefined);

/**
 * Index of the active header navigation item (links + controls, in render order).
 * -1 means "unknown yet" so no button is marked selected until Header's pathname-matching
 * effect resolves the real index - avoids flashing the wrong button on mount.
 */
export const headerActiveAtom = atom<number>(-1);
export const topLevelPageReadyAtom = atom<boolean>(true);

export const skipAutoFocusOnFrontpageAtom = atom<boolean>(false);

export const npvrNotificationStateAtom = atom<NpvrNotificationStateInfo | undefined>(undefined);

// When true (developer test mode), real get/set notification API calls are skipped so they
// don't overwrite the atom with live backend data while testing.
export const npvrNotificationTestModeAtom = atom<boolean>(false);

export const npvrFailedDialogResolvedAtom = atom<boolean>(false);

// Tracks the last explore universe selected, so focus can be restored when returning without router state
export const exploreLastSelectedTitleAtom = atom<string | undefined>(undefined);

/**
 * Atom for tracking the current page type ('frontpage', 'universe', or 'provider-universe')
 */
export const pageTypeAtom = atom<'frontpage' | 'universe' | 'provider-universe'>('frontpage');

// Network traffic logger (developer tool)
export interface NetworkLogEntry {
  id: number;
  timestamp: string;
  cmd: string;
  method: string;
  status: 'pending' | 'success' | 'error';
  duration?: number;
  error?: unknown;
  params?: Record<string, unknown>;
  response?: unknown;
}

export const networkLogEnabledAtom = atom<boolean>(false);
export const networkLogEntriesAtom = atom<NetworkLogEntry[]>([]);

export interface NPVRStreamState {
  episodeId?: number;
  length?: number;
  progress?: number;
  focusKey?: string;
  fromFocusEpisode?: boolean;
}

export const NPVRStreamAtom = atom<NPVRStreamState | undefined>(undefined);

export const isStreamingAtom = atom<boolean>(false);
export const playerFocusKeyAtom = atom<string | null>(null);

export interface TvGuideProgramRecordingStatusUpdate {
  isRecording: boolean;
  sid: number;
  seriesId?: number;
}

export interface TvGuideSeriesRecordingStatusUpdate {
  isRecording: boolean;
  startTime?: number;
}

export interface TvGuideRecordingStatusUpdates {
  programs: Record<string, TvGuideProgramRecordingStatusUpdate>;
  series: Record<string, TvGuideSeriesRecordingStatusUpdate>;
}

export const tvGuideRecordingStatusUpdatesAtom = atom<TvGuideRecordingStatusUpdates>({
  programs: {},
  series: {}
});

export interface ToastMessage {
  message: string;
  iconType: IconTypes;
}

export const toastAtom = atom<ToastMessage | null>(null);
export const isErrorDialogOpenAtom = atom<boolean>(false);
export const errorAtom = atom<{
  err: BaseError | CmdError | KMMError;
  prevFocusKey: string;
  isStreamPlaying: boolean;
  playerFocusKey: string | null;
} | null>(null);
